import { ChevronDown, ChevronUp, Plus, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatBRDateTime } from '@control-center/shared';

import { cn } from '@control-center/ui';

import type { ClientSaasLink } from '../../lib/saas-links-api';
import { useConfirmSaasLink, useRejectSaasLink } from '../../hooks/use-saas-links';
import { extractErrorMessage } from '../../lib/errors';

interface AwaitingReviewCardProps {
  link: ClientSaasLink;
}

interface CandidateBreakdown {
  clientId: string;
  totalScore: number;
  breakdown: {
    document: number;
    authId: number;
    email: number;
    nameSimilarity: number;
    phone: number;
    cityState: number;
  };
}

function isCandidate(value: unknown): value is CandidateBreakdown {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.clientId === 'string' && typeof v.totalScore === 'number';
}

function extractCandidates(breakdown: unknown): CandidateBreakdown[] {
  if (!breakdown || typeof breakdown !== 'object') return [];
  const b = breakdown as Record<string, unknown>;
  const list = b.candidates;
  if (!Array.isArray(list)) return [];
  return list.filter(isCandidate);
}

function CriterionRow({ label, score, weight }: { label: string; score: number; weight: number }) {
  const matched = score > 0;
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-[6px] border px-2 py-1 text-[11px]',
        matched ? 'border-good/40 bg-good/10 text-good' : 'border-line bg-panel-3 text-muted-3',
      )}
    >
      <span>
        {matched ? '✓' : '✗'} {label}
      </span>
      <span className="font-mono tnum">
        +{score}/{weight}
      </span>
    </div>
  );
}

export function AwaitingReviewCard({ link }: AwaitingReviewCardProps): JSX.Element {
  const confirmMut = useConfirmSaasLink();
  const rejectMut = useRejectSaasLink();
  const candidates = extractCandidates(link.matchBreakdown);
  const [showOthers, setShowOthers] = useState(false);

  const topScore = link.matchScore ?? candidates[0]?.totalScore ?? 0;
  const topBreakdown = candidates[0]?.breakdown ?? null;

  async function handleConfirm() {
    try {
      await confirmMut.mutateAsync(link.id);
      toast.success(`Vínculo confirmado: ${link.client.name}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao confirmar.'));
    }
  }

  async function handleReject() {
    const reason = window.prompt('Motivo (opcional):', 'Não é o mesmo cliente');
    if (reason === null) return;
    try {
      await rejectMut.mutateAsync({ id: link.id, reason: reason || null });
      toast.success('Vínculo rejeitado. Próximo aparece após re-matching.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao rejeitar.'));
    }
  }

  return (
    <article className="flex flex-col gap-4 rounded-[12px] border border-line bg-panel p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-3">
            Cadastro recebido — {link.saasProduct.name}
          </div>
          <div className="mt-1 font-mono text-[12px] text-ink">
            tenant: {link.externalTenantId ?? '—'} · user: {link.externalUserId ?? '—'}
          </div>
          {link.externalEmail && (
            <div className="text-[12px] text-muted-2">{link.externalEmail}</div>
          )}
          {link.externalDocument && (
            <div className="font-mono text-[11px] text-muted-2">doc: {link.externalDocument}</div>
          )}
          <div className="mt-1 text-[11px] text-muted-3">
            Recebido em {formatBRDateTime(link.linkedAt)} · via{' '}
            <span className="font-mono">{link.linkedVia}</span>
          </div>
        </div>
        <div className="rounded-[8px] border border-warn/40 bg-warn/10 px-3 py-2 text-right">
          <div className="text-[10px] uppercase tracking-wider text-warn">Score</div>
          <div className="font-mono text-[18px] font-semibold text-ink">{topScore}/130</div>
        </div>
      </header>

      <div className="rounded-[10px] border border-line bg-panel-2 p-3">
        <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-3">
          Sugestão (top 1)
        </div>
        <Link
          to={`/identities/${link.client.id}`}
          className="text-[14px] font-semibold text-ink underline-offset-2 hover:underline"
        >
          {link.client.name}
        </Link>
        {link.client.email && <div className="text-[12px] text-muted-2">{link.client.email}</div>}
        {link.client.document && (
          <div className="font-mono text-[11px] text-muted-2">{link.client.document}</div>
        )}
        {topBreakdown && (
          <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            <CriterionRow label="Documento" score={topBreakdown.document} weight={50} />
            <CriterionRow label="Auth ID" score={topBreakdown.authId} weight={60} />
            <CriterionRow label="Email" score={topBreakdown.email} weight={40} />
            <CriterionRow label="Nome" score={topBreakdown.nameSimilarity} weight={20} />
            <CriterionRow label="Telefone" score={topBreakdown.phone} weight={15} />
            <CriterionRow label="Cidade+UF" score={topBreakdown.cityState} weight={5} />
          </div>
        )}
      </div>

      {candidates.length > 1 && (
        <button
          type="button"
          onClick={() => setShowOthers((v) => !v)}
          className="inline-flex items-center gap-1 self-start rounded-[6px] border border-line bg-panel-2 px-2 py-1 text-[11px] text-muted hover:text-ink"
        >
          {showOthers ? (
            <ChevronUp size={12} strokeWidth={1.8} />
          ) : (
            <ChevronDown size={12} strokeWidth={1.8} />
          )}
          Ver outros candidatos (scores:{' '}
          {candidates
            .slice(1)
            .map((c) => c.totalScore)
            .join(', ')}
          )
        </button>
      )}

      {showOthers && candidates.length > 1 && (
        <div className="flex flex-col gap-2">
          {candidates.slice(1).map((cand) => (
            <div
              key={cand.clientId}
              className="rounded-[8px] border border-line bg-panel-2 p-2 text-[12px]"
            >
              <Link
                to={`/identities/${cand.clientId}`}
                className="text-ink underline-offset-2 hover:underline"
              >
                Cliente {cand.clientId.slice(0, 8)}…
              </Link>{' '}
              <span className="text-muted-2">— score {cand.totalScore}/130</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
        <Link
          to={`/identities/new?fromSaasLink=${link.id}`}
          className="inline-flex h-8 items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-3 text-[12px] text-ink hover:bg-panel-3"
        >
          <Plus size={13} strokeWidth={1.8} />
          Criar cliente novo
        </Link>
        <button
          type="button"
          onClick={() => void handleReject()}
          disabled={rejectMut.isPending}
          className="inline-flex h-8 items-center gap-1 rounded-[6px] border border-bad/40 bg-bad/10 px-3 text-[12px] text-bad hover:bg-bad/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <UserX size={13} strokeWidth={1.8} />É outro cliente
        </button>
        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={confirmMut.isPending}
          className="inline-flex h-8 items-center gap-1 rounded-[6px] border border-line bg-ink px-3 text-[12px] disabled:cursor-not-allowed disabled:opacity-50"
          style={{ color: '#09090b' }}
        >
          <UserCheck size={13} strokeWidth={1.8} />
          Confirmar vínculo
        </button>
      </div>
    </article>
  );
}
